import { Box, Text, Skeleton } from '@mantine/core'
import { useRouter } from 'next/router'

import { useQueryGetBookingById } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { valueFromCents } from '@flecto/ui/lib/utils'

import { DiscountLabel } from '../components/DiscountLabel'
import { DiscountValue } from '../components/DiscountValue'
import { TaxesValue } from '../components/TaxesValue/TaxesValue'
import classes from '../styles.module.css'

export const useRows = () => {
  const sanitizeValue = (text: number | undefined) => {
    return valueFromCents(text ?? 0).currency
  }

  const router = useRouter()

  const id = Number.isNaN(Number(router.query.id))
    ? ''
    : Number(router.query.id)

  const { data, isLoading } = useQueryGetBookingById({ bookingId: id })

  const { t } = useTranslation('saas', { keyPrefix: 'booking_sidebar' })

  const rows = [
    {
      label: <Text fw={500}>{t('overview.total')}</Text>,
      classes: [classes.highlighted, classes.total],
      element: (
        <SkeletonWrapper isLoading={isLoading}>
          <Text fw={500}>{sanitizeValue(data?.meta?.rental_total_cents)}</Text>
        </SkeletonWrapper>
      ),
    },
    {
      label: <Text>{t('overview.subtotal')}</Text>,
      element: (
        <SkeletonWrapper isLoading={isLoading}>
          <Text fw={400}>
            {sanitizeValue(data?.meta?.rental_subtotal_cents)}
          </Text>
        </SkeletonWrapper>
      ),
    },
    {
      label: <Text>{t('overview.vat')}</Text>,
      element: (
        <SkeletonWrapper isLoading={isLoading}>
          <TaxesValue booking={data} />
        </SkeletonWrapper>
      ),
    },

    {
      label: <Text>{t('overview.subtotal_incl_tax')}</Text>,
      element: (
        <SkeletonWrapper isLoading={isLoading}>
          <Text fw={400}>
            {sanitizeValue(data?.meta?.rental_total_without_discount_cents)}
          </Text>
        </SkeletonWrapper>
      ),
    },
    {
      label: <DiscountLabel amount={data?.discount_amount ?? 0} />,
      element: (
        <SkeletonWrapper isLoading={isLoading}>
          <Box mr={-8}>
            <DiscountValue
              bookingId={data?.internal_id ?? 0}
              discount_cents={`-${sanitizeValue(data?.meta?.discount_cents)}`}
            />
          </Box>
        </SkeletonWrapper>
      ),
    },
  ]

  if (data?.meta?.kept_deposit_cents || data?.meta?.deposit_cents) {
    rows.push(
      {
        label: <Text fw={500}>{t('overview.deposit')}</Text>,
        classes: [classes.highlighted, classes.deposit],
        element: (
          <SkeletonWrapper isLoading={isLoading}>
            <Text fw={500}>{sanitizeValue(data?.meta?.deposit_cents)}</Text>
          </SkeletonWrapper>
        ),
      },
      {
        label: <Text>{t('overview.kept')}</Text>,
        element: (
          <SkeletonWrapper isLoading={isLoading}>
            <Text fw={400}>
              {sanitizeValue(data?.meta?.kept_deposit_cents)}
            </Text>
          </SkeletonWrapper>
        ),
      }
    )
  }

  return { rows }
}

type SkeletonProps = {
  isLoading: boolean
  children: React.ReactNode
}
const SkeletonWrapper = ({ children, isLoading }: SkeletonProps) => {
  return isLoading ? (
    <Skeleton width={60} height={14} />
  ) : (
    <span>{children}</span>
  )
}
